// filter-url-state.js
//
// Read filter/sort selections from the URL query string and write them
// back when they change. Used by gallery components so filter selections
// survive refresh and can be shared via copied URL.
//
// IMPORTANT: This file is bundled into the SSR React payload that runs
// on ChakraCore. No optional chaining (?.), nullish coalescing (??), or
// logical-assignment operators (??=, ||=, &&=). See CLAUDE.md
// "ChakraCore Compatibility" footgun.
//
// Schema vocabulary (passed by each gallery):
//   'array'  -> URL value is CSV; state value is array of trimmed strings
//   'number' -> URL value is a numeric string; state value is a number
//   'string' -> URL value is a string; state value is a string
//
// Empty/default values are omitted from the URL. The caller's filter
// state object can have other keys; only keys named in the schema are
// serialized.

var DEFAULT_PRESERVE = ['q', 'ref'];

function isBrowser() {
    return typeof window !== 'undefined' && typeof window.history !== 'undefined';
}

function readQueryPairs(search) {
    var out = {};
    if (!search || search.length < 2) return out;
    var query = search.charAt(0) === '?' ? search.substring(1) : search;
    var pairs = query.split('&');
    for (var i = 0; i < pairs.length; i++) {
        var pair = pairs[i];
        if (!pair) continue;
        var eqIdx = pair.indexOf('=');
        var key, rawVal;
        if (eqIdx === -1) {
            key = safeDecode(pair);
            rawVal = '';
        } else {
            key = safeDecode(pair.substring(0, eqIdx));
            rawVal = safeDecode(pair.substring(eqIdx + 1).replace(/\+/g, ' '));
        }
        out[key] = rawVal;
    }
    return out;
}

function safeDecode(s) {
    try { return decodeURIComponent(s); }
    catch (e) { return s; }
}

function parseValue(raw, type) {
    if (raw == null || raw === '') return null;
    if (type === 'array') {
        var parts = raw.split(',');
        var out = [];
        for (var i = 0; i < parts.length; i++) {
            var v = parts[i].trim();
            if (v.length > 0) out.push(v);
        }
        return out.length > 0 ? out : null;
    }
    if (type === 'number') {
        var n = Number(raw);
        if (isNaN(n)) return null;
        return n;
    }
    // 'string' (default)
    var s = String(raw).trim();
    return s.length > 0 ? s : null;
}

function serializeValue(val, type) {
    if (val == null) return null;
    if (type === 'array') {
        if (!Array.isArray(val) || val.length === 0) return null;
        var parts = [];
        for (var i = 0; i < val.length; i++) {
            if (val[i] == null) continue;
            var s = String(val[i]).trim();
            if (s.length > 0) parts.push(s);
        }
        return parts.length > 0 ? parts.join(',') : null;
    }
    if (type === 'number') {
        if (typeof val === 'number') {
            if (isNaN(val)) return null;
            return String(val);
        }
        var n = Number(val);
        if (isNaN(n)) return null;
        return String(n);
    }
    // 'string'
    var str = String(val).trim();
    return str.length > 0 ? str : null;
}

// Read filter state from window.location.search.
// Returns an object with one key per schema entry (only when the URL
// carried a non-empty value for that key), plus `_preserved` — any
// query keys not listed in the schema, so writeFiltersToUrl can later
// restore them. Safe to call during SSR (returns { _preserved: {} }).
export function parseFiltersFromUrl(schema) {
    var out = { _preserved: {} };
    if (!isBrowser()) return out;
    var pairs = readQueryPairs(window.location.search);
    var pairKeys = Object.keys(pairs);
    for (var i = 0; i < pairKeys.length; i++) {
        var key = pairKeys[i];
        if (schema && Object.prototype.hasOwnProperty.call(schema, key)) {
            var parsed = parseValue(pairs[key], schema[key]);
            if (parsed != null) out[key] = parsed;
        } else {
            out._preserved[key] = pairs[key];
        }
    }
    return out;
}

// Write filter state to the URL via history.replaceState. Schema keys
// are emitted in declaration order; empty/default values are omitted.
// Reserved query keys (default ['q', 'ref'], override via opts.preserve)
// are carried over from the current URL unchanged.
//
// No-op when not in a browser and no-op when the URL would not change
// (avoids redundant history entries and popstate fires).
export function writeFiltersToUrl(state, schema, opts) {
    if (!isBrowser()) return;
    var preserve = (opts && opts.preserve) ? opts.preserve : DEFAULT_PRESERVE;
    var existing = readQueryPairs(window.location.search);

    var out = [];
    // Preserved keys come first so user-recognizable params (`q` especially)
    // lead the URL — e.g. ?q=2001&types=Frames... — instead of being buried
    // after the filter params.
    for (var p = 0; p < preserve.length; p++) {
        var pk = preserve[p];
        if (Object.prototype.hasOwnProperty.call(existing, pk)) {
            var pv = existing[pk];
            if (pv !== '') {
                out.push(encodeURIComponent(pk) + '=' + encodeURIComponent(pv));
            } else {
                out.push(encodeURIComponent(pk));
            }
        }
    }
    if (schema) {
        var keys = Object.keys(schema);
        for (var k = 0; k < keys.length; k++) {
            var sk = keys[k];
            var serialized = serializeValue(state ? state[sk] : null, schema[sk]);
            if (serialized != null) {
                out.push(encodeURIComponent(sk) + '=' + encodeURIComponent(serialized));
            }
        }
    }

    var newSearch = out.length > 0 ? '?' + out.join('&') : '';
    var current = window.location.pathname + window.location.search + window.location.hash;
    var newUrl = window.location.pathname + newSearch + window.location.hash;
    if (newUrl !== current) {
        window.history.replaceState(window.history.state, '', newUrl);
    }
}

// Shallow per-element equality for arrays. Used by gallery syncUrl to
// suppress emitting the `sort` URL param when the current sort still
// matches the gallery's DEFAULT_SORT.
export function arraysShallowEqual(a, b) {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
    }
    return true;
}

// Case-insensitively rewrite each value in `arr` to its canonical-case
// equivalent from `canonicals` if one matches. Used to normalize
// URL-pasted values like `?industry=residential` → `["Residential"]` so
// downstream case-sensitive indexOf checks (chip rendering, dropdown
// checkbox state, filter predicate) all match. Pass-through for values
// with no canonical match.
export function normalizeArray(arr, canonicals) {
    if (!arr || !canonicals || !arr.length || !canonicals.length) return arr;
    var out = [];
    for (var i = 0; i < arr.length; i++) {
        var lower = String(arr[i]).toLowerCase();
        var matched = null;
        for (var j = 0; j < canonicals.length; j++) {
            if (String(canonicals[j]).toLowerCase() === lower) { matched = canonicals[j]; break; }
        }
        out.push(matched !== null ? matched : arr[i]);
    }
    return out;
}

// Subscribe a callback to browser back/forward navigation. Returns an
// unsubscriber. Safe no-op during SSR.
export function subscribePopstate(callback) {
    if (!isBrowser() || typeof callback !== 'function') {
        return function () {};
    }
    window.addEventListener('popstate', callback);
    return function () {
        window.removeEventListener('popstate', callback);
    };
}
